import React, { useState, useEffect } from 'react';
import Button from '../../../ui/Button';
import axiosInstance from '../../../api/axiosInstance';
import logger from '../../../../utils/logger';

export default function DomainMappingStep({ users, domainMapping, onUpdate, onNext }) {
  const [mapping, setMapping] = useState(domainMapping || {});
  const [targetDomains, setTargetDomains] = useState([]);
  const [loading, setLoading] = useState(true);

  // Extract source domains from users
  const sourceDomains = [...new Set(users.map(u => u.domain || u.email?.split('@')[1]).filter(Boolean))];

  useEffect(() => {
    const fetchDomains = async () => {
      try {
        const response = await axiosInstance.get('/api/business/domains');
        if (response.data.success) {
          setTargetDomains(response.data.domains || []);
        }
      } catch (err) {
        logger.error('Failed to fetch domains:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchDomains();
  }, []);

  // Normalize a target-domain entry to its domain string. The domains API
  // returns objects keyed `domainName`; older/other shapes may use name/domain.
  const targetDomainName = (td) =>
    typeof td === 'string' ? td : (td?.domainName || td?.name || td?.domain || '');

  // Auto-map matching domains
  useEffect(() => {
    if (targetDomains.length > 0 && Object.keys(mapping).length === 0) {
      const autoMapping = {};
      sourceDomains.forEach(sd => {
        if (!sd) return;
        const match = targetDomains.find(td => {
          const tdName = targetDomainName(td);
          return tdName && tdName.toLowerCase() === sd.toLowerCase();
        });
        if (match) {
          autoMapping[sd] = targetDomainName(match);
        }
      });
      if (Object.keys(autoMapping).length > 0) {
        setMapping(autoMapping);
      }
    }
  }, [targetDomains, sourceDomains.join(',')]); // eslint-disable-line

  const handleMappingChange = (sourceDomain, targetDomain) => {
    setMapping(prev => ({ ...prev, [sourceDomain]: targetDomain }));
  };

  const handleContinue = () => {
    onUpdate(mapping);
    onNext();
  };

  const allMapped = sourceDomains.every(sd => mapping[sd]);

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Map Domains</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Map each source domain to a target domain on your mail server
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <svg className="w-6 h-6 text-orange-500 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth={4} className="opacity-25" />
            <path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" className="opacity-75" />
          </svg>
          <span className="ml-2 text-sm text-gray-500">Loading domains...</span>
        </div>
      ) : (
        <div className="space-y-3">
          {sourceDomains.map(sd => (
            <div key={sd} className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
              {/* Source */}
              <div className="flex-1 min-w-0">
                <label className="block text-xs text-gray-500 mb-1">Source Domain</label>
                <div className="text-sm font-medium text-gray-900 dark:text-white truncate">{sd}</div>
              </div>

              {/* Arrow */}
              <svg className="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>

              {/* Target */}
              <div className="flex-1 min-w-0">
                <label className="block text-xs text-gray-500 mb-1">Target Domain</label>
                <select
                  value={mapping[sd] || ''}
                  onChange={(e) => handleMappingChange(sd, e.target.value)}
                  className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                >
                  <option value="">Select target domain</option>
                  {targetDomains.map(td => {
                    const name = targetDomainName(td);
                    return name ? <option key={name} value={name}>{name}</option> : null;
                  })}
                  {/* Allow same domain if not in list */}
                  {!targetDomains.some(td => targetDomainName(td) === sd) && (
                    <option value={sd}>{sd} (same)</option>
                  )}
                </select>
              </div>

              {/* Status */}
              <div className="flex-shrink-0">
                {mapping[sd] ? (
                  <svg className="w-5 h-5 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                )}
              </div>
            </div>
          ))}

          {sourceDomains.length === 0 && (
            <p className="text-sm text-gray-500 py-4 text-center">No source domains found. Continue to proceed.</p>
          )}

          <Button
            variant="primary"
            onClick={handleContinue}
            disabled={sourceDomains.length > 0 && !allMapped}
          >
            Continue
          </Button>
        </div>
      )}
    </div>
  );
}
